import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Monthly trust reconciliation (O. Reg. 579/05 s.13): bank vs trust books vs deal balances,
// prepared within 30 days, reviewed, signed and dated by the broker of record.
const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';
const monthName = (p) => p ? new Date(`${p}-15T12:00:00`).toLocaleDateString('en-CA', { month:'long', year:'numeric' }) : '';
const STATUS = { signed:['ok', 'Signed'], draft:['warn', 'Awaiting signature'], missing:['neutral', 'Not started'] };

export default function TrustReconciliation({ BASE, headers, p, dark, canPrepare, canSign }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period:'', bank_closing:'', statement_received_date:'', bank_csv:'', fileName:'' });
  const [preview, setPreview] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [viewing, setViewing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/reconciliations`, { headers }).then(r => r.ok ? r.json() : null).then(setData).catch(() => setData(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/reconciliations${path}`, { method:'POST', headers, body: JSON.stringify(body || {}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };
  const body = () => {
    const b = { period: form.period };
    if (form.bank_closing !== '') b.bank_closing = String(form.bank_closing);
    if (form.statement_received_date) b.statement_received_date = form.statement_received_date;
    if (form.bank_csv) b.bank_csv = form.bank_csv;
    if (preview) { b.deposits_in_transit = preview.deposits_in_transit; b.outstanding_cheques = preview.outstanding_cheques; }
    if (explanation.trim()) b.explanation = explanation.trim();
    return b;
  };

  const start = (period) => {
    setForm({ period, bank_closing:'', statement_received_date:'', bank_csv:'', fileName:'' });
    setPreview(null); setExplanation(''); setOpen(true);
  };
  const runCheck = async () => {
    const b = body(); delete b.deposits_in_transit; delete b.outstanding_cheques;
    const d = await post('/preview', b);
    if (d) setPreview(d);
  };
  const dropItem = (key, idx) => setPreview(pv => {
    const items = pv[key].filter((_, i) => i !== idx);
    const total = items.reduce((s, i) => s + i.amount_cents, 0);
    const dit = key === 'deposits_in_transit' ? total : pv.deposits_in_transit_cents;
    const oc = key === 'outstanding_cheques' ? total : pv.outstanding_cheques_cents;
    const adjusted = pv.bank_closing_cents + dit - oc;
    return { ...pv, [key]: items, deposits_in_transit_cents: dit, outstanding_cheques_cents: oc, adjusted_bank_cents: adjusted, bank_vs_book_cents: adjusted - pv.book_balance_cents, _edited: true };
  });
  const save = async () => {
    const d = await post('', body());
    if (d) { toast.success(canSign ? 'Saved. Review and sign it below.' : 'Saved for the broker of record to sign'); setOpen(false); load(); if (canSign) setViewing(d); }
  };
  const openSaved = async (id) => {
    const res = await fetch(`${BASE}/api/accounting/trust/reconciliations/${id}`, { headers });
    if (!res.ok) return toast.error('Couldn’t open that reconciliation');
    const d = await res.json(); setExplanation(d.explanation || ''); setViewing(d);
  };
  const sign = async () => {
    const d = await post(`/${viewing.id}/sign`, { explanation: explanation.trim() || undefined });
    if (d) { toast.success(d.signed_late ? 'Signed. It was past the 30-day deadline, so it shows as late.' : 'Signed and dated'); setViewing(d); load(); }
  };
  const exportCsv = async (r) => {
    const res = await fetch(`${BASE}/api/accounting/trust/reconciliations/${r.id}/export.csv`, { headers });
    if (!res.ok) return toast.error('Export failed');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `trust-reconciliation-${r.period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const readFile = (file) => {
    if (!file) return;
    if (file.size > 600000) return toast.error('That file is too large. Export one month at a time.');
    const reader = new FileReader();
    reader.onload = () => { setForm(f => ({ ...f, bank_csv: String(reader.result || ''), fileName: file.name })); setPreview(null); };
    reader.readAsText(file);
  };

  if (!data) return null;
  const months = data.months || [];
  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const small = (color) => ({ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color });
  const row = (label, cents, strong, tone) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', fontSize:13, borderTop: strong ? `1px solid ${p.hair}` : 'none', fontWeight: strong ? 500 : 400 }}>
      <span style={{ color: strong ? p.ink : p.ink2 }}>{label}</span><span style={{ fontFamily:F.mono, color: tone || p.ink }}>{cad(cents)}</span>
    </div>
  );
  const summary = (r) => (<>
    <div style={{ border:`1px solid ${p.hair}`, borderRadius:2, padding:'12px 16px' }}>
      {row('Bank closing balance', r.bank_closing_cents)}
      {row('Add: deposits in transit', r.deposits_in_transit_cents)}
      {row('Less: outstanding cheques and EFTs', r.outstanding_cheques_cents)}
      {row('Adjusted bank balance', r.adjusted_bank_cents, true)}
      {row('Trust book balance', r.book_balance_cents, true)}
      {row('Sum of deal balances', r.subledger_total_cents, true)}
      {row('Bank vs books difference', r.bank_vs_book_cents, true, r.bank_vs_book_cents ? T.bad : T.ok)}
    </div>
    {(r.needs_explanation || []).length > 0 ? (
      <div style={{ fontSize:12, color:T.bad, lineHeight:1.6 }}>{r.needs_explanation.map(n => <div key={n}>{n}</div>)}</div>
    ) : (r.adjusted_bank_cents === r.book_balance_cents && <div style={{ fontSize:12, color:T.ok }}>Bank, books and deal balances agree.</div>)}
  </>);
  const items = (title, list, key, editable) => (list || []).length > 0 && (
    <div><Over color={p.ink3}>{title}</Over>
      {list.map((i, idx) => (
        <div key={`${i.entry_id || i.row || i.description}-${idx}`} style={{ display:'flex', justifyContent:'space-between', gap:10, fontSize:12, padding:'6px 0', borderBottom:`1px solid ${p.hair}` }}>
          <span style={{ color:p.ink2, minWidth:0 }}>{day(i.book_date || i.date)} · {i.description || '—'}</span>
          <span style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
            <span style={{ fontFamily:F.mono }}>{cad(i.amount_cents)}</span>
            {editable && <button style={small(p.ink3)} onClick={() => dropItem(key, idx)}>Cleared</button>}
          </span>
        </div>
      ))}
    </div>
  );
  const people = (subs) => (subs || []).length > 0 && (
    <div><Over color={p.ink3}>Balance held for each person</Over>
      {subs.map((s, idx) => (
        <div key={`${s.transaction_id}-${idx}`} style={{ display:'flex', justifyContent:'space-between', gap:10, fontSize:12, padding:'6px 0', borderBottom:`1px solid ${p.hair}` }}>
          <span style={{ color:p.ink2 }}>{s.property_address}{(s.held_for || []).length ? ` · ${s.held_for.join(', ')}` : ''}</span>
          <span style={{ fontFamily:F.mono, color: s.balance_cents < 0 ? T.bad : p.ink }}>{cad(s.balance_cents)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ border:`1px solid ${data.overdue_count ? T.bad : p.hair}`, borderRadius:2 }}>
      <div style={{ padding:'12px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12 }}>
        <Over color={data.overdue_count ? T.bad : p.ink3}>Monthly reconciliation</Over>
        <span style={{ fontSize:12, color: data.overdue_count ? T.bad : p.ink3 }}>
          {data.overdue_count ? `${data.overdue_count} month${data.overdue_count === 1 ? '' : 's'} past the 30-day deadline` : 'Due 30 days after each bank statement'}
        </span>
      </div>
      {months.length === 0 && <div style={{ padding:'20px var(--bx-pad)', fontSize:13, color:p.ink3 }}>The first reconciliation opens once the account’s first month ends.</div>}
      {months.map((m, i) => {
        const [tone, label] = STATUS[m.status] || STATUS.missing;
        return (
          <div key={m.period} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, padding:'10px var(--bx-pad)', borderBottom:i === months.length - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13 }}>
            <div style={{ minWidth:0 }}>
              <div>{monthName(m.period)}</div>
              <div style={{ fontSize:11, color: m.overdue ? T.bad : p.ink3, marginTop:2 }}>
                {m.status === 'signed' ? `Signed by ${m.signed_by_name || '—'} · ${day(m.signed_at)}${m.signed_late ? ' · late' : ''}` : `Due ${day(m.due)}${m.overdue ? ' · overdue' : ''}`}
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
              <Pill tone={tone} dark={dark}>{label}</Pill>
              {m.id && <button style={small(p.ink2)} onClick={() => openSaved(m.id)}>Open</button>}
              {canPrepare && m.status !== 'signed' && <button style={small(T.brick)} onClick={() => start(m.period)}>{m.status === 'draft' ? 'Redo' : 'Reconcile'}</button>}
            </div>
          </div>
        );
      })}

      {/* Prepare */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent style={{ maxWidth:560, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)', maxHeight:'90vh', overflowY:'auto' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>Reconcile {monthName(form.period)}</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>Upload the trust account’s bank statement as CSV, or type the closing balance. Bricks matches it to the approved trust entries and checks bank against books against each deal’s balance.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Bank statement CSV (optional)</Over>
              <input type="file" accept=".csv,text/csv" onChange={e => readFile(e.target.files?.[0])} style={{ ...input, padding:'8px 10px' }}/>
              {form.fileName && <div style={{ fontSize:11, color:p.ink3, marginTop:4 }}>{form.fileName}</div>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><Over color={p.ink3}>Closing balance (CAD)</Over><input type="number" step="0.01" style={{ ...input, fontFamily:F.mono }} value={form.bank_closing} onChange={e => { setForm({ ...form, bank_closing: e.target.value }); setPreview(null); }} placeholder={form.bank_csv ? 'From the file' : '0.00'}/></div>
              <div><Over color={p.ink3}>Statement received</Over><input type="date" style={input} value={form.statement_received_date} onChange={e => { setForm({ ...form, statement_received_date: e.target.value }); setPreview(null); }}/></div>
            </div>
            {!preview && <Btn variant="primary" dark={dark} disabled={busy} onClick={runCheck}>Run the check</Btn>}
            {preview && (<>
              {summary(preview)}
              {(preview._warnings || []).map(w => <div key={w} style={{ fontSize:12, color:T.warn }}>{w}</div>)}
              {preview.bank_line_count > 0 && <div style={{ fontSize:12, color:p.ink3 }}>{preview._matched_count} of {preview.bank_line_count} bank lines matched to trust entries{preview._carried_count ? `, ${preview._carried_count} item(s) carried from last month` : ''}.</div>}
              {items('Deposits in transit', preview.deposits_in_transit, 'deposits_in_transit', true)}
              {items('Outstanding cheques and EFTs', preview.outstanding_cheques, 'outstanding_cheques', true)}
              {items('Bank items not in the trust books', preview.unrecorded_bank_items)}
              {people(preview.subledgers)}
              {(preview.needs_explanation || []).length > 0 && (
                <div><Over color={p.ink3}>Explain the differences</Over>
                  <textarea rows={3} style={{ ...input, resize:'vertical' }} value={explanation} onChange={e => setExplanation(e.target.value)} placeholder="e.g. $4.95 bank fee charged to trust in error; brokerage top-up posted Oct 3"/>
                </div>
              )}
            </>)}
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setOpen(false)}>Cancel</Btn>
            {preview && <Btn variant="primary" dark={dark} disabled={busy} onClick={save}>Save for signature</Btn>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review / sign */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }}>
        <DialogContent style={{ maxWidth:560, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)', maxHeight:'90vh', overflowY:'auto' }}>
          {viewing && (<>
            <DialogHeader>
              <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>{monthName(viewing.period)} reconciliation</DialogTitle>
              <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>
                Prepared by {viewing.prepared_by_name || '—'} · {day(viewing.prepared_at)}. Due {day(viewing.due_date)}.
                {viewing.status === 'signed' ? ` Signed by ${viewing.signed_by_name} on ${day(viewing.signed_at)}. Signed reconciliations can’t be changed.` : ' The broker of record reviews, signs and dates it.'}
              </DialogDescription>
            </DialogHeader>
            <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
              {summary(viewing)}
              {items('Deposits in transit', viewing.deposits_in_transit)}
              {items('Outstanding cheques and EFTs', viewing.outstanding_cheques)}
              {items('Bank items not in the trust books', viewing.unrecorded_bank_items)}
              {people(viewing.subledgers)}
              {viewing.status === 'signed' ? (viewing.explanation && <div style={{ fontSize:13, color:p.ink2 }}><Over color={p.ink3}>Explanation</Over><div style={{ marginTop:6 }}>{viewing.explanation}</div></div>)
                : canSign && (viewing.needs_explanation || []).length > 0 && (
                <div><Over color={p.ink3}>Explain the differences</Over>
                  <textarea rows={3} style={{ ...input, resize:'vertical' }} value={explanation} onChange={e => setExplanation(e.target.value)}/>
                </div>
              )}
            </div>
            <DialogFooter style={{ marginTop:22, gap:10 }}>
              <Btn variant="ghost" dark={dark} onClick={() => exportCsv(viewing)}>Export CSV</Btn>
              {canSign && viewing.status === 'draft' && <Btn variant="primary" dark={dark} disabled={busy} onClick={sign}>Sign and date</Btn>}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
